import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col, Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getEmailTemplate, createEmailTemplate, updateEmailTemplate, getGlobalEmailPlaceholders } from '../../services/api';

const EmailTemplateEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === undefined || id === 'new';

    const [template, setTemplate] = useState({
        name: '',
        type: '',
        subject: '',
        body_html: '',
        body_plain: '',
        placeholders: [],
    });
    const [globalPlaceholders, setGlobalPlaceholders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchGlobalPlaceholders = async () => {
            try {
                const response = await getGlobalEmailPlaceholders();
                setGlobalPlaceholders(response.data || []);
            } catch (err) {
                console.error("Errore nel recupero dei placeholder globali:", err);
                // Non bloccare l'utente per questo, ma logga l'errore
            }
        };

        fetchGlobalPlaceholders();

        if (!isNew) {
            const templateIdNum = Number(id);
            if (isNaN(templateIdNum)) {
                setError("ID template non valido.");
                return;
            }
            
            setLoading(true);
            setError(null);
            const fetchTemplate = async () => {
                try {
                    const response = await getEmailTemplate(templateIdNum);
                    setTemplate(response.data);
                } catch (err) {
                    console.error("Errore nel recupero del template email:", err);
                    setError("Impossibile caricare il template email.");
                    toast.error("Errore nel caricamento del template.");
                } finally {
                    setLoading(false);
                }
            };
            fetchTemplate();
        }
    }, [id, isNew]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setTemplate(prev => ({ ...prev, [name]: value }));
    };

    const handlePlaceholdersChange = (e) => {
        setTemplate(prev => ({ ...prev, placeholders: e.target.value.split(',').map(p => p.trim()).filter(p => p) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            if (isNew) {
                await createEmailTemplate(template);
                toast.success("Template email creato con successo!");
            } else {
                await updateEmailTemplate(id, template);
                toast.success("Template email aggiornato con successo!");
            }
            navigate('/email-templates');
        } catch (err) {
            console.error("Errore nel salvataggio del template:", err);
            setError("Errore nel salvataggio del template email.");
            toast.error("Errore nel salvataggio del template.");
        } finally {
            setLoading(false);
        }
    };
    
    // Combina i placeholder specifici e globali per la visualizzazione
    const allPlaceholders = [...new Set([...(template.placeholders || []), ...globalPlaceholders])];

    if (loading && !isNew) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <h1 className="h3 mb-2 text-gray-800">{isNew ? 'Nuovo Template Email' : `Modifica Template: ${template.name}`}</h1>
                    <p className="mb-4">Gestisci i dettagli del template email.</p>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Card shadow="sm">
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Nome Template</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={template.name}
                                        onChange={handleChange}
                                        required
                                        disabled={!isNew}
                                    />
                                    {!isNew && <Form.Text className="text-muted">Il nome non può essere modificato dopo la creazione.</Form.Text>}
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="type">
                                    <Form.Label>Tipo Template</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="type"
                                        value={template.type}
                                        onChange={handleChange}
                                        required
                                    />
                                    <Form.Text className="text-muted">Es: order_confirmation, admin_order_notification, quote_request</Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="subject">
                                    <Form.Label>Oggetto Email</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="subject"
                                        value={template.subject}
                                        onChange={handleChange}
                                        required
                                    />
                                    <Form.Text className="text-muted">Utilizza placeholder come <code>{'{{azienda.denominazione}}'}</code> nel soggetto.</Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="body_html">
                                    <Form.Label>Corpo HTML</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={10}
                                        name="body_html"
                                        value={template.body_html}
                                        onChange={handleChange}
                                        required
                                    />
                                    <Form.Text className="text-muted">Utilizza Jinja2 per i placeholder, es. <code>{'{{cliente.nome}}'}</code>. Supporta HTML completo.</Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="body_plain">
                                    <Form.Label>Corpo Testo Semplice</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="body_plain"
                                        value={template.body_plain}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="placeholders_info">
                                    <Form.Label>Placeholder Disponibili</Form.Label>
                                    <Card bg="light" text="dark" className="p-3">
                                        <p style={{ fontFamily: 'monospace', fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                            {allPlaceholders.join(', ')}
                                        </p>
                                    </Card>
                                    <Form.Text className="text-muted">
                                        Questi sono i placeholder che puoi usare nell'oggetto e nel corpo dell'email.
                                    </Form.Text>
                                </Form.Group>
                                
                                <Form.Group className="mb-3" controlId="placeholders">
                                    <Form.Label>Placeholder Specifici del Template (separati da virgola)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="placeholders"
                                        value={template.placeholders ? template.placeholders.join(', ') : ''}
                                        onChange={handlePlaceholdersChange}
                                    />
                                    <Form.Text className="text-muted">
                                       Definisci qui solo i placeholder unici per questo tipo di template (es. id_ordine, nome_prodotto). I placeholder globali come quelli aziendali sono già inclusi.
                                    </Form.Text>
                                </Form.Group>

                                <Button variant="primary" type="submit" disabled={loading}>
                                    {loading ? <Spinner animation="border" size="sm" /> : (isNew ? 'Crea Template' : 'Salva Modifiche')}
                                </Button>
                                <Button variant="secondary" onClick={() => navigate('/email-templates')} className="ms-2">
                                    Annulla
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default EmailTemplateEdit;